import React, { useRef } from 'react';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useTranslation } from 'react-i18next';
import { UtilityProofFileSchema, UtilityType } from '../../types/utility-proof.type';
import { ACCEPTED_FILE_TYPES } from '../../services/utility-proof.service';
import UtilityFileList from './UtilityFileList';

export interface UtilityProofRowProps {
  utilityType: UtilityType;
  label: string;
  Icon: React.ComponentType<SvgIconProps>;
  files: UtilityProofFileSchema[];
  isRequired: boolean;
  isLocked: boolean;
  isUploading: boolean;
  uploadError: string | null;
  onClearError: () => void;
  onUpload: (files: File[]) => void;
}

const UtilityProofRow: React.FC<UtilityProofRowProps> = ({
  label,
  Icon,
  files,
  isRequired,
  isLocked,
  isUploading,
  uploadError,
  onClearError,
  onUpload,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasFiles = files.length > 0;

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files ? Array.from(e.target.files) : [];
    if (selected.length > 0) onUpload(selected);
    e.target.value = '';
  };

  const statusChip = isRequired ? (
    hasFiles ? (
      <Chip
        icon={<CheckCircleOutlinedIcon />}
        label={t('dataEntry.proofs.complete')}
        size="small"
        color="success"
        variant="outlined"
        sx={{ fontSize: '0.7rem', height: 22 }}
      />
    ) : (
      <Chip
        icon={<WarningAmberOutlinedIcon />}
        label={t('dataEntry.proofs.missing')}
        size="small"
        color="warning"
        variant="outlined"
        sx={{ fontSize: '0.7rem', height: 22 }}
      />
    )
  ) : (
    <Chip
      label={t('dataEntry.proofs.optional')}
      size="small"
      variant="outlined"
      sx={{
        fontSize: '0.7rem',
        height: 22,
        color: 'text.secondary',
        borderColor: 'divider',
      }}
    />
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          py: 2,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        {/* Icon + label + status */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            minWidth: 148,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.08),
              flexShrink: 0,
            }}
          >
            <Icon sx={{ color: 'primary.main', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {label}
            </Typography>
            <Box sx={{ mt: 0.4 }}>{statusChip}</Box>
          </Box>
        </Box>

        {/* File list */}
        <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
          <UtilityFileList files={files} />
        </Box>

        {/* Upload button */}
        {!isLocked && (
          <Box sx={{ flexShrink: 0, pt: 0.25 }}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_FILE_TYPES}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={
                isUploading ? (
                  <CircularProgress size={13} color="inherit" />
                ) : (
                  <CloudUploadOutlinedIcon />
                )
              }
              onClick={handleButtonClick}
              disabled={isUploading}
              sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}
            >
              {isUploading ? t('dataEntry.proofs.uploading') : t('dataEntry.proofs.upload')}
            </Button>
          </Box>
        )}
      </Box>

      {uploadError && (
        <Alert
          severity="error"
          onClose={onClearError}
          sx={{ mb: 1, py: 0.5, fontSize: '0.8rem' }}
        >
          {uploadError}
        </Alert>
      )}
    </Box>
  );
};

export default UtilityProofRow;
